export type Customer = {
  customerId: number;
  userEmail: string;
  userName: string;
  status: CustomerStatus;
  tableExtractionEnabled: boolean;
};

export enum CustomerStatus {
  ACT = "ACT",
  MDEL = "MDEL",
  DEL = "DEL",
  EXP = "EXP"
}

export const getStatusLabel = (status: CustomerStatus): string => {
  switch (status) {
    case CustomerStatus.ACT:
      return "Active";
    case CustomerStatus.MDEL:
      return "Marked as deleted";
    case CustomerStatus.DEL:
      return "Deleted";
    case CustomerStatus.EXP:
      return "Expired";
  }
};

export const getValidTransitions = (status: CustomerStatus): CustomerStatus[] => {
  switch (status) {
    case CustomerStatus.ACT:
      return [CustomerStatus.EXP];
    case CustomerStatus.EXP:
      return [CustomerStatus.ACT];
    case CustomerStatus.MDEL:
      return [];
    case CustomerStatus.DEL:
      return [];
  }
};

export type Corpus = {
  corpusId: number;
  enabled: boolean;
  corpusName: string;
};

export type Job = {
  jobId: number;
  typeId: number;
  corpusId: number;
  description: string;
  params: string;
  status: string;
  comment: string;
};

export type Plan = {
  planId: string;
  planName: string;
  planDescription: string;
  planJson: string;
  startDate: number;
  expiryDate: number;
  planType: PlanType;
};

export enum PlanType {
  PLAN_NAME__FREE_TRIAL = "PLAN_NAME__FREE_TRIAL",
  PLAN_NAME__SCALE = "PLAN_NAME__SCALE"
}

export type AuthUser = {
  email: string;
  role: string;
  permissions: Array<string>;
};

export type Stats = {
  queryCount: number;
  summarizations: number;
  gpt4GenerativeRequests: number;
  bytesIngested: number;
  storageUsed: number;
  users: number;
  corpora: number;
  month: string;
  year: number;
  startEpoch: number;
  endEpoch: number;
};

export enum StatsModels {
  GPT_4 = "gpt-4",
  OTHERS = "*"
}

export type ScaleCustomer = {
  id: number;
  name: string;
};

export enum FeatureType {
  FEATURE__UNKNOWN = 0,
  FEATURE__RECEIVE_SCORE = 1,
  FEATURE__QUERY_BG_VEC = 2,
  FEATURE__CUSTOM_DIMENSIONS = 3,
  FEATURE__ENCODER_SWAPPING = 4,
  FEATURE__TEXTLESS = 5,
  FEATURE__USER_RATE_LIMIT = 6,
  FEATURE__CORPUS_RATE_LIMIT = 7,
  FEATURE__CORPUS_ENCRYPTION_KEY = 8,
  FEATURE__CUSTOMER_MANAGED_ENCRYPTION_KEY = 9,
  FEATURE__DOCUMENT_METADATA = 10,
  FEATURE__DOCUMENT_PART_METADATA = 11,
  FEATURE__PREDICATE_FILTERING = 12
}

export type PricingPlanPeriod = {
  years: number;
  months: number;
  days: number;
};

export type PricingPlanPrice = {
  amount: number;
  currency: string;
};

export type PricingPlanFeature = {
  feature: keyof typeof FeatureType;
};

export type PricingPlan = {
  enabledFeatures: Array<PricingPlanFeature>;
  includedQueries: {
    amount: number;
    period: PricingPlanPeriod;
  };
  queryOverage: {
    amount: number;
    price: PricingPlanPrice;
  };
  includedIndexing: {
    amount: number;
    period: PricingPlanPeriod;
  };
  indexingOverage: {
    amount: number;
    price: PricingPlanPrice;
  };
  includedStorage: number;
  storageOverage: {
    amount: number;
    price: PricingPlanPrice;
  };
  includedCorpora: number;
  corpusOverage: {
    amount: number;
    price: PricingPlanPrice;
  };
  queryStorageIngestBundle?: {
    ingestBytes?: number;
    period?: PricingPlanPeriod;
    price?: PricingPlanPrice;
    queries?: number;
    storageBytes?: number;
    summarizations?: number;
  };
  includedUsers: number;
  userOverage: {
    amount: number;
    price: PricingPlanPrice;
  };
  includedApiKeys: number;
  includedSummarizations: {
    amount: number;
    period: PricingPlanPeriod;
  };
  base: {
    amount: PricingPlanPrice;
    period: PricingPlanPeriod;
  };
  servingReplicas: number;
  maxRerankRows: number;
  maxResultRows: number;
  maxFilterAttrs: number;
  maxIndexedFilterAttrs: number;
  maxAttrsPerFilter: number;
  maxFilterSize: number;
  maxFilterLength: number;
};

export type Encoder = {
  name: string;
  description: string;
  cxSpec: string;
  cxSpecServing: string;
  dimension: number;
  default: boolean;
};

export type PromptType = "PLAIN" | "VELOCITY";

export type PromptObj = {
  type: PromptType;
  text: string;
};

export type PromptConfigParams = {
  frequency_penalty: number;
  max_tokens: number;
  presence_penalty: number;
  temperature: number;
  top_p: number;
};

export type Model = {
  modelId: number;
  modelName: string;
};

export type Prompt = {
  name: string;
  description: string;
  model: Model;
  isDefault: boolean;
  prompt: PromptObj;
  configParams: PromptConfigParams;
};

export type AWSMPCustomer = {
  customerId: number;
  name: string;
  userEmail: string;
  awsId: string;
  awsAccountId: string;
  tsContractStart: number;
  tsContractExpire: number;
  awsProductCode: string;
  planId: string;
  planName: string;
};

export type FailedDoc = {
  id: number;
  documentId: string;
  customerId: number;
  corpusId: number;
  originalStatusCode: number;
  originalStatusText: string;
  mimeType: string;
  locationType: string;
  locationPath: string;
  locationBucket: string;
  api: string;
};

export type ScalePlanDefaults = {
  maxCorpora: number;
  maxResultRows: number;
  maxRerankRows: number;
  maxBytes: number;
};

export type PricingPlanDetails = {
  planId: string;
  name: string;
  planSpec: any;
};

export type Field = {
  value: string;
  isValid: boolean;
  error?: string;
};

export type PlanParameters = FreeTrialPlanParameters | ScalePlanParameters;

export type FreeTrialPlanParameters = {
  expiryEpochSecond: number;
};

export type ScalePlanParameters = {
  effectiveFromEpochSecond: number;
  tableExtractionEnabled: boolean;
  maxBytes: number;
};

export type User = {
  id: number;
  username: string;
  organization: string;
  description: string;
  roles: string[];
};

export type Role = {
  id: number;
  name: string;
  description: string;
  permissions: string[];
};

export type Permission = {
  id: number;
  name: string;
  description: string;
};

export type CustomerEnvironment = {
  maxBytes: number;
};

export type BillingPeriod = {
  id: number;
  customer_id: number;
  period_start: number;
  period_end: number;
  amount_due: number;
  currency: string;
  status: string;
  stripe_invoice_id?: string;
  stripe_payment_id?: string;
  comment?: string;
  created_at: number;
  updated_at: number;
};

export type BillingPeriodDetail = {
  id: number;
  billing_period_id: number;
  customer_id: number;
  pricing_agreement_id: number;
  customer_plan_id?: string;
  plan_type?: string;
  dt_start?: number;
  dt_end?: number;
  item_description: string;
  amount: number;
  currency: string;
  comment?: string;
  // Added for UI display purposes
  quantity: number;
  unit_price: number;
  created_at: number;
  updated_at: number;
};

export type ScheduledPayment = {
  id: number;
  customer_id: number;
  scheduled_date: number;
  processed_date?: number;
  amount: number;
  currency: string;
  status: string;
  description: string;
  comment?: string;
  billing_period_id?: number;
  attempt_ctr?: number;
  retry_ctr?: number;
  created_at: number;
  updated_at: number;
};

export type BillingData = {
  billing_periods: BillingPeriod[];
  billing_period_details: BillingPeriodDetail[];
  scheduled_payments: ScheduledPayment[];
};